import { useCallback, useEffect, useRef } from 'react';

const CELL_SIZE = 55;
const INFLUENCE_RADIUS = 260;
const MAX_WARP = 24;
const DOT_SPACING = 28;
const LERP_SPEED = 0.08;
const RIPPLE_INTERVAL = 1400;
const DEFAULT_THEME = {
  background: '#161618',
  lineColor: '#4A9EFF',
  dotColor: '#4A9EFF',
  warpGlow: '#4A9EFF',
  rippleColor: '#64B4FF',
};

function lerp(a, b, t) {
  return a + (b - a) * t;
}

function lerpColor(base, active, t) {
  const r = Math.round(lerp(base.r, active.r, t));
  const g = Math.round(lerp(base.g, active.g, t));
  const b = Math.round(lerp(base.b, active.b, t));
  const a = lerp(base.a, active.a, t);
  return `rgba(${r},${g},${b},${a.toFixed(3)})`;
}

function hexToRgb(hex) {
  const value = hex.replace('#', '');
  const normalized = value.length === 3 ? value.split('').map((part) => `${part}${part}`).join('') : value;
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
}

function getAutomaticFocus(now, width, height) {
  const time = now / 1000;
  return {
    x: width * (0.5 + 0.36 * Math.sin(time * 0.45) + 0.08 * Math.sin(time * 0.23 + 1.2)),
    y: height * (0.5 + 0.32 * Math.sin(time * 0.62 + 1.4) + 0.06 * Math.cos(time * 0.29)),
  };
}

export default function KineticGrid({ children, className = '', globalColor = 'default', theme: customTheme }) {
  const canvasRef = useRef(null);
  const focusRef = useRef({ x: -9999, y: -9999 });
  const targetFocusRef = useRef({ x: -9999, y: -9999 });
  const ripplesRef = useRef([]);
  const lastRippleRef = useRef(0);
  const rafRef = useRef(0);
  const sizeRef = useRef({ w: 0, h: 0 });

  const getWarpedPoint = useCallback((gx, gy, col, row, focus, ripples, cols, rows) => {
    const edgeMargin = 1.5;
    const colPin = Math.min(col / edgeMargin, (cols - 1 - col) / edgeMargin, 1);
    const rowPin = Math.min(row / edgeMargin, (rows - 1 - row) / edgeMargin, 1);
    const pinFactor = colPin * colPin * rowPin * rowPin;
    const dx = gx - focus.x;
    const dy = gy - focus.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const proximity = Math.max(0, 1 - dist / INFLUENCE_RADIUS) * pinFactor;
    let rx = 0;
    let ry = 0;

    ripples.forEach((ripple) => {
      const rdx = gx - ripple.x;
      const rdy = gy - ripple.y;
      const rdist = Math.sqrt(rdx * rdx + rdy * rdy);
      const diff = rdist - ripple.radius;
      const waveWidth = 55;
      if (Math.abs(diff) < waveWidth) {
        const strength = (1 - Math.abs(diff) / waveWidth) * ripple.opacity * 18 * pinFactor;
        const angle = Math.atan2(rdy, rdx);
        const sign = diff < 0 ? -1 : 1;
        rx += Math.cos(angle) * strength * sign * -1;
        ry += Math.sin(angle) * strength * sign * -1;
      }
    });

    if (dist < INFLUENCE_RADIUS && dist > 0 && pinFactor > 0) {
      const t = dist / INFLUENCE_RADIUS;
      const eased = t < 0.01 ? 0 : (1 - t) * (1 - t) * Math.min(1, dist / 60);
      const warpAmount = eased * MAX_WARP * pinFactor;
      const angle = Math.atan2(dy, dx);
      return {
        point: { x: gx - Math.cos(angle) * warpAmount + rx, y: gy - Math.sin(angle) * warpAmount + ry },
        proximity,
      };
    }

    return { point: { x: gx + rx, y: gy + ry }, proximity };
  }, []);

  const draw = useCallback((now) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    if (!context) return;
    const { w: width, h: height } = sizeRef.current;
    const focus = focusRef.current;
    const ripples = ripplesRef.current;
    const colorTheme = customTheme || (globalColor === 'monochrome'
      ? { background: '#000000', lineColor: '#FFFFFF', dotColor: '#FFFFFF', warpGlow: '#FFFFFF', rippleColor: '#FFFFFF' }
      : DEFAULT_THEME);
    const lineColor = hexToRgb(colorTheme.lineColor);
    const dotColor = hexToRgb(colorTheme.dotColor);
    const warpGlow = hexToRgb(colorTheme.warpGlow);
    const rippleColor = hexToRgb(colorTheme.rippleColor);
    const theme = {
      bg: colorTheme.background,
      lineBase: { ...lineColor, a: colorTheme.lineOpacity || 0.18 },
      lineActive: { ...lineColor, a: 0.72 },
      nodeBase: { ...dotColor, a: 0.24 },
      nodeActive: { ...dotColor, a: 1 },
      glow: `${warpGlow.r},${warpGlow.g},${warpGlow.b}`,
      ripple: `${rippleColor.r},${rippleColor.g},${rippleColor.b}`,
    };

    context.clearRect(0, 0, width, height);
    context.fillStyle = theme.bg;
    context.fillRect(0, 0, width, height);
    context.fillStyle = 'rgba(255,255,255,0.05)';
    for (let x = DOT_SPACING / 2; x < width; x += DOT_SPACING) {
      for (let y = DOT_SPACING / 2; y < height; y += DOT_SPACING) {
        context.beginPath();
        context.arc(x, y, 0.7, 0, Math.PI * 2);
        context.fill();
      }
    }

    for (let index = ripples.length - 1; index >= 0; index -= 1) {
      const ripple = ripples[index];
      const age = (now - ripple.born) / 1000;
      ripple.radius = Math.max(0, age * 400);
      ripple.opacity = Math.max(0, 1 - age * 1.2);
      if (ripple.opacity <= 0) ripples.splice(index, 1);
    }

    const cols = Math.max(2, Math.ceil(width / CELL_SIZE)) + 1;
    const rows = Math.max(2, Math.ceil(height / CELL_SIZE)) + 1;
    const cellWidth = width / (cols - 1);
    const cellHeight = height / (rows - 1);
    const points = [];
    const proximities = [];

    for (let row = 0; row < rows; row += 1) {
      points[row] = [];
      proximities[row] = [];
      for (let col = 0; col < cols; col += 1) {
        const warped = getWarpedPoint(col * cellWidth, row * cellHeight, col, row, focus, ripples, cols, rows);
        points[row][col] = warped.point;
        proximities[row][col] = warped.proximity;
      }
    }

    const drawSegment = (pointA, pointB, proximityA, proximityB) => {
      const average = (proximityA + proximityB) / 2;
      const active = average * average * (3 - 2 * average);
      context.beginPath();
      context.moveTo(pointA.x, pointA.y);
      context.lineTo(pointB.x, pointB.y);
      context.strokeStyle = lerpColor(theme.lineBase, theme.lineActive, active);
      context.lineWidth = lerp(1, 1.5, active);
      context.stroke();
    };

    context.lineCap = 'butt';
    for (let row = 0; row < rows; row += 1) {
      for (let col = 0; col < cols - 1; col += 1) drawSegment(points[row][col], points[row][col + 1], proximities[row][col], proximities[row][col + 1]);
    }
    for (let col = 0; col < cols; col += 1) {
      for (let row = 0; row < rows - 1; row += 1) drawSegment(points[row][col], points[row + 1][col], proximities[row][col], proximities[row + 1][col]);
    }

    for (let row = 0; row < rows; row += 1) {
      for (let col = 0; col < cols; col += 1) {
        const point = points[row][col];
        const proximity = proximities[row][col];
        const active = proximity * proximity * (3 - 2 * proximity);
        const radius = lerp(1.8, 3.2, active);
        if (active > 0.3) {
          const glowRadius = radius + lerp(0, 6, (active - 0.3) / 0.7);
          const gradient = context.createRadialGradient(point.x, point.y, radius * 0.5, point.x, point.y, glowRadius);
          gradient.addColorStop(0, `rgba(${theme.glow},${(active * 0.3).toFixed(3)})`);
          gradient.addColorStop(1, `rgba(${theme.glow},0)`);
          context.beginPath();
          context.arc(point.x, point.y, glowRadius, 0, Math.PI * 2);
          context.fillStyle = gradient;
          context.fill();
        }
        context.beginPath();
        context.arc(point.x, point.y, radius, 0, Math.PI * 2);
        context.fillStyle = lerpColor(theme.nodeBase, theme.nodeActive, active);
        context.fill();
      }
    }

    ripples.forEach((ripple) => {
      context.beginPath();
      context.arc(ripple.x, ripple.y, Math.max(0, ripple.radius), 0, Math.PI * 2);
      context.strokeStyle = `rgba(${theme.ripple},${(ripple.opacity * 0.28).toFixed(3)})`;
      context.lineWidth = 1.5;
      context.stroke();
    });
  }, [customTheme, getWarpedPoint, globalColor]);

  const animate = useCallback((now) => {
    const { w: width, h: height } = sizeRef.current;
    const focus = focusRef.current;
    const target = targetFocusRef.current;

    if (width && height) {
      const automaticFocus = getAutomaticFocus(now, width, height);
      target.x = automaticFocus.x;
      target.y = automaticFocus.y;

      if (now - lastRippleRef.current >= RIPPLE_INTERVAL) {
        ripplesRef.current.push({ x: target.x, y: target.y, radius: 0, opacity: 1, born: now });
        lastRippleRef.current = now;
      }
    }

    focus.x = lerp(focus.x, target.x, LERP_SPEED);
    focus.y = lerp(focus.y, target.y, LERP_SPEED);
    draw(now);
    rafRef.current = requestAnimationFrame(animate);
  }, [draw]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const setSize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
      sizeRef.current = { w: width, h: height };
      if (focusRef.current.x < 0) {
        const initialFocus = { x: width / 2, y: height / 2 };
        focusRef.current = initialFocus;
        targetFocusRef.current = initialFocus;
      }
    };
    setSize();
    window.addEventListener('resize', setSize);
    rafRef.current = requestAnimationFrame(animate);
    return () => {
      window.removeEventListener('resize', setSize);
      cancelAnimationFrame(rafRef.current);
    };
  }, [animate]);

  return (
    <div className={className} style={{ position: 'relative', width: '100%', minHeight: '100vh', overflow: 'hidden', backgroundColor: customTheme?.background || (globalColor === 'monochrome' ? '#000000' : DEFAULT_THEME.background) }}>
      <canvas ref={canvasRef} aria-hidden='true' style={{ position: 'fixed', inset: 0, width: '100%', height: '100%', zIndex: 0, pointerEvents: 'none' }} />
      <div style={{ position: 'relative', zIndex: 1, width: '100%', minHeight: '100%' }}>{children}</div>
    </div>
  );
}
